import { useState, useRef, useCallback } from "react";
import { toast } from "react-toastify";
import ASSETS from "../../api/services/ASSETS";

// DocumentUploadModal
//
// Lets the admin upload one or more files for a specific client.
// Mirrors the RN Admin-App "UploadDocuments" bottom sheet so the
// two surfaces stay feature-equal:
//   - drag-and-drop OR file picker
//   - multi-file (up to MAX_PER_BATCH at a time)
//   - per-file rename + optional comment
//   - server-side limit is 20 files per client (enforced by backend
//     too — we just show a warning here so admins know).
//
// Upload flow:
//   1. POST createSignedUploadURL with the list of file extensions
//      → backend returns presigned PUT URLs + matching fileMetas
//      (assetId + s3FilePath) per file.
//   2. PUT each File blob directly to its presigned URL.
//   3. POST saveClientDocuments with the resulting `files[]` payload.
//
// On success the modal closes and the parent's onUploaded callback
// fires so it can refetch the document list.

const MAX_PER_BATCH = 6;

const DocumentUploadModal = ({ userId, onClose, onUploaded }) => {
  const fileInputRef = useRef(null);
  // Each item: { file: File, name: string, comment: string }
  const [items, setItems] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const addFiles = useCallback((fileList) => {
    const incoming = Array.from(fileList || []);
    if (!incoming.length) return;
    setItems((prev) => {
      const free = MAX_PER_BATCH - prev.length;
      if (free <= 0) {
        toast.warn(`ניתן להעלות עד ${MAX_PER_BATCH} קבצים בבת אחת`);
        return prev;
      }
      const accepted = incoming.slice(0, free).map((f) => ({
        file: f,
        // Default the display name to the file's base name (no extension).
        name: f.name.replace(/\.[^.]+$/, ""),
        comment: "",
      }));
      if (incoming.length > free) {
        toast.warn(`נוספו ${free} קבצים, השאר עברו את המגבלה`);
      }
      return [...prev, ...accepted];
    });
  }, []);

  const handleFilePick = (e) => {
    addFiles(e.target.files);
    // Reset so picking the same file twice in a row still triggers change.
    e.target.value = "";
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    addFiles(e.dataTransfer?.files);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    if (!dragOver) setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const updateItem = (idx, field, value) => {
    setItems((prev) =>
      prev.map((it, i) => (i === idx ? { ...it, [field]: value } : it))
    );
  };

  const removeItem = (idx) => {
    setItems((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleUpload = async () => {
    if (!items.length) {
      toast.error("אין קבצים להעלאה");
      return;
    }
    if (items.some((it) => !it.name.trim())) {
      toast.error("יש למלא שם לכל קובץ");
      return;
    }
    setUploading(true);
    try {
      // Step 1 — ask the backend to mint presigned URLs for each
      // file extension. Extensions must include the leading dot.
      const fileExtensions = items.map((it) => {
        const m = it.file.name.match(/(\.[^.]+)$/);
        return m ? m[1].toLowerCase() : "";
      });
      if (fileExtensions.some((e) => !e)) {
        toast.error("אחד הקבצים ללא סיומת — לא ניתן להעלות");
        setUploading(false);
        return;
      }

      const signedRes = await ASSETS.createSignedUploadURL(userId, fileExtensions);
      const uploadURLs = signedRes?.data?.data?.uploadURLs;
      const fileMetas = signedRes?.data?.data?.fileMetas;
      if (!Array.isArray(uploadURLs) || !Array.isArray(fileMetas) || uploadURLs.length !== items.length) {
        throw new Error("Invalid signed URL response");
      }

      // Step 2 — direct PUT to S3 for every file in parallel.
      const uploadedUrls = await Promise.all(
        items.map((it, i) => ASSETS.uploadToS3(uploadURLs[i], it.file))
      );

      // Step 3 — persist the documents on the user's profile.
      const filesPayload = items.map((it, i) => ({
        url: uploadedUrls[i],
        fileName: it.name.trim(),
        comment: it.comment.trim(),
        assetId: fileMetas[i].assetId,
        s3FilePath: fileMetas[i].s3FilePath,
      }));

      const saveRes = await ASSETS.saveClientDocuments(userId, filesPayload);
      if (saveRes?.data?.code !== 200) {
        throw new Error(saveRes?.data?.message || "שמירה נכשלה");
      }

      toast.success("הקבצים הועלו בהצלחה ✅");
      onUploaded?.();
      onClose?.();
    } catch (e) {
      console.error("Upload failed:", e);
      toast.error(e?.response?.data?.message || e?.message || "שגיאה בהעלאה");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div
      onClick={(e) => e.target === e.currentTarget && !uploading && onClose()}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.55)",
        zIndex: 10000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
      }}
    >
      <div
        style={{
          background: "#fff",
          borderRadius: 16,
          width: "100%",
          maxWidth: 560,
          maxHeight: "90vh",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          direction: "rtl",
        }}
      >
        {/* Header */}
        <div
          style={{
            background: "linear-gradient(135deg,#DE1468,#ff6b9d)",
            padding: "16px 20px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div>
            <div style={{ color: "#fff", fontWeight: 700, fontSize: 16 }}>
              העלאת מסמכים
            </div>
            <div style={{ color: "rgba(255,255,255,0.85)", fontSize: 12, marginTop: 2 }}>
              עד {MAX_PER_BATCH} קבצים בבת אחת · לכל לקוח עד 20 בסך הכל
            </div>
          </div>
          <button
            onClick={() => !uploading && onClose()}
            disabled={uploading}
            style={{
              background: "rgba(255,255,255,0.2)",
              border: "1px solid rgba(255,255,255,0.4)",
              color: "#fff",
              borderRadius: 8,
              padding: "6px 12px",
              fontSize: 14,
              cursor: uploading ? "not-allowed" : "pointer",
              opacity: uploading ? 0.6 : 1,
            }}
          >
            ✕
          </button>
        </div>

        {/* Body */}
        <div style={{ overflowY: "auto", padding: 20, display: "flex", flexDirection: "column", gap: 14 }}>

          {/* Drop zone */}
          <div
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onClick={() => fileInputRef.current?.click()}
            style={{
              border: `2px dashed ${dragOver ? "#DE1468" : "#cbd5e1"}`,
              background: dragOver ? "#fff5f9" : "#f8fafc",
              borderRadius: 12,
              padding: "26px 16px",
              textAlign: "center",
              cursor: "pointer",
              transition: "all 0.15s",
            }}
          >
            <div style={{ fontSize: 30, marginBottom: 6 }}>☁️</div>
            <div style={{ fontSize: 14, fontWeight: 700, color: "#1e293b" }}>
              גרור קבצים לכאן או לחץ לבחירה
            </div>
            <div style={{ fontSize: 12, color: "#64748b", marginTop: 4 }}>
              PDF · תמונות · Word · Excel · עד {MAX_PER_BATCH} בבת אחת
            </div>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.gif,.txt,.heic"
              onChange={handleFilePick}
              style={{ display: "none" }}
            />
          </div>

          {/* Selected file list */}
          {items.length > 0 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {items.map((it, idx) => (
                <div
                  key={idx}
                  style={{
                    background: "#f8fafc",
                    border: "1px solid #e2e8f0",
                    borderRadius: 10,
                    padding: 12,
                    display: "flex",
                    flexDirection: "column",
                    gap: 8,
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12, color: "#64748b", textAlign: "right" }}>
                        📄 {it.file.name} ({(it.file.size / 1024).toFixed(0)} KB)
                      </div>
                    </div>
                    <button
                      onClick={() => removeItem(idx)}
                      disabled={uploading}
                      style={{
                        background: "none",
                        border: "none",
                        color: "#94a3b8",
                        cursor: uploading ? "not-allowed" : "pointer",
                        fontSize: 16,
                      }}
                      title="הסר"
                    >
                      ✕
                    </button>
                  </div>
                  <input
                    type="text"
                    value={it.name}
                    onChange={(e) => updateItem(idx, "name", e.target.value)}
                    placeholder="שם להצגה"
                    dir="rtl"
                    disabled={uploading}
                    style={{
                      border: "1px solid #e2e8f0",
                      borderRadius: 8,
                      padding: "8px 10px",
                      fontSize: 13,
                      fontFamily: "inherit",
                      color: "#111",
                      textAlign: "right",
                    }}
                  />
                  <input
                    type="text"
                    value={it.comment}
                    onChange={(e) => updateItem(idx, "comment", e.target.value)}
                    placeholder="הערה (אופציונלי)"
                    dir="rtl"
                    disabled={uploading}
                    style={{
                      border: "1px solid #e2e8f0",
                      borderRadius: 8,
                      padding: "8px 10px",
                      fontSize: 12,
                      fontFamily: "inherit",
                      color: "#475569",
                      textAlign: "right",
                    }}
                  />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          style={{
            borderTop: "1px solid #f1f5f9",
            padding: "12px 20px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 10,
          }}
        >
          <button
            onClick={onClose}
            disabled={uploading}
            style={{
              background: "#f1f5f9",
              border: "none",
              borderRadius: 10,
              padding: "10px 18px",
              fontSize: 13,
              fontWeight: 700,
              color: "#475569",
              cursor: uploading ? "not-allowed" : "pointer",
              opacity: uploading ? 0.6 : 1,
            }}
          >
            ביטול
          </button>
          <button
            onClick={handleUpload}
            disabled={uploading || items.length === 0}
            style={{
              background: uploading || items.length === 0 ? "#f1f5f9" : "#DE1468",
              color: uploading || items.length === 0 ? "#94a3b8" : "#fff",
              border: "none",
              borderRadius: 10,
              padding: "10px 24px",
              fontSize: 13,
              fontWeight: 700,
              cursor: uploading || items.length === 0 ? "not-allowed" : "pointer",
            }}
          >
            {uploading
              ? "מעלה..."
              : items.length === 0
              ? "בחר קבצים תחילה"
              : `📤 העלה ${items.length} ${items.length === 1 ? "קובץ" : "קבצים"}`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DocumentUploadModal;
